import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight, faShieldAlt, faChartLine, faUsers, faBell,
  faCalendarAlt, faMoneyBillWave, faCheckCircle, faChevronLeft,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import AkasbLogo from "../assets/img/logo_akasb.png";

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

function Reveal({ children, delay = 0, from = "bottom", style = {} }) {
  const [ref, inView] = useInView();
  const transforms = { bottom: "translateY(44px)", left: "translateX(-44px)", right: "translateX(44px)" };
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? "none" : transforms[from],
      transition: `opacity 0.75s ease ${delay}s, transform 0.75s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
}

const FEATURES = [
  { icon: faUsers,          color: "#4f8ef7", title: "Gestão de Praticantes", desc: "Consulte fichas, graduações, presenças e evolução de cada atleta em tempo real." },
  { icon: faCalendarAlt,    color: "#E4B428", title: "Aulas & Eventos",        desc: "Horários, confirmações de presença, campeonatos e atividades do clube." },
  { icon: faMoneyBillWave,  color: "#34d399", title: "Pagamentos",             desc: "Controlo de quotas, histórico financeiro e notificações automáticas." },
  { icon: faChartLine,      color: "#e05252", title: "Dashboard Completo",     desc: "Visão geral do clube com estatísticas e relatórios para a direção." },
  { icon: faBell,           color: "#a78bfa", title: "Comunicações",           desc: "Mensagens e notificações instantâneas entre treinadores e encarregados." },
  { icon: faShieldAlt,      color: "#34d399", title: "Segurança Total",        desc: "Acesso controlado por perfil — direção, treinadores e encarregados." },
];

const PROFILES = [
  { role: "Encarregados de Educação", emoji: "👨‍👩‍👧", items: ["Acompanhe a evolução do seu educando", "Consulte horários e presenças", "Receba notificações do clube", "Confirme inscrições em eventos", "Gerencie pagamentos de quotas"] },
  { role: "Direção & Treinadores",    emoji: "🥋", items: ["Dashboard com estatísticas em tempo real", "Gestão completa de praticantes", "Controlo financeiro do clube", "Comunicações com famílias", "Organização de eventos e turmas"] },
];

export default function Connect() {
  const [scrollY, setScrollY] = useState(0);
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    const h = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Anton&family=Bebas+Neue&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,600;1,9..40,300&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin:0; padding:0; }
        html { scroll-behavior: smooth; }
        body { font-family: 'DM Sans', sans-serif; background: #0d0b1e; overflow-x: hidden; color: #fff; }
        img { display: block; max-width: 100%; }
        a { text-decoration: none; color: inherit; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #0d0b1e; }
        ::-webkit-scrollbar-thumb { background: #E4B428; border-radius: 2px; }

        @keyframes fadeUp      { from { opacity:0; transform:translateY(48px); } to { opacity:1; transform:none; } }
        @keyframes fadeLeft    { from { opacity:0; transform:translateX(60px); } to { opacity:1; transform:none; } }
        @keyframes floatY      { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes rotateSlow  { to { transform:rotate(360deg); } }
        @keyframes marquee     { from{transform:translateX(0)} to{transform:translateX(-50%)} }
        @keyframes glowPulse   { 0%,100%{opacity:.4} 50%{opacity:.9} }
        @keyframes barGrow     { from{width:0} to{width:100%} }
        @keyframes shimmerSlide{
          0%   { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }

        .anim-badge  { animation: fadeUp   .9s cubic-bezier(.16,1,.3,1) .2s  both; }
        .anim-title  { animation: fadeLeft  1s cubic-bezier(.16,1,.3,1) .4s  both; }
        .anim-sub    { animation: fadeUp   .9s cubic-bezier(.16,1,.3,1) .65s both; }
        .anim-btns   { animation: fadeUp   .9s cubic-bezier(.16,1,.3,1) .9s  both; }
        .anim-float  { animation: floatY 4s ease-in-out infinite; }
        .anim-glow   { animation: glowPulse 3s ease-in-out infinite; }
        .anim-ring   { animation: rotateSlow 28s linear infinite; }
        .ticker-wrap { overflow:hidden; }
        .ticker-inner{ display:flex; width:max-content; animation:marquee 40s linear infinite; }

        .feature-card { transition: transform .35s cubic-bezier(.16,1,.3,1), box-shadow .35s ease, border-color .35s; cursor:default; }
        .feature-card:hover { transform:translateY(-8px); }

        .profile-card { transition: transform .3s ease, box-shadow .3s ease; }
        .profile-card:hover { transform:translateY(-6px); }

        .cta-btn-primary {
          display:inline-flex; align-items:center; justify-content:center; gap:10px;
          padding: clamp(14px,2.5vw,18px) clamp(28px,5vw,52px);
          background:#E4B428; color:#0d0b1e;
          border-radius:3px; font-weight:800; letter-spacing:2px; text-transform:uppercase;
          font-size:clamp(12px,1.5vw,14px);
          box-shadow:0 8px 40px rgba(228,180,40,.4);
          transition:all .25s;
          white-space:nowrap;
        }
        .cta-btn-primary:hover { transform:translateY(-3px); box-shadow:0 16px 56px rgba(228,180,40,.55); }

        .cta-btn-secondary {
          display:inline-flex; align-items:center; justify-content:center; gap:10px;
          padding: clamp(14px,2.5vw,18px) clamp(28px,5vw,52px);
          background:rgba(255,255,255,.06); color:rgba(255,255,255,.85);
          border-radius:3px; font-weight:700; letter-spacing:2px; text-transform:uppercase;
          font-size:clamp(12px,1.5vw,14px);
          border:1.5px solid rgba(255,255,255,.18);
          transition:all .25s;
          white-space:nowrap;
        }
        .cta-btn-secondary:hover { background:rgba(255,255,255,.12); border-color:rgba(255,255,255,.4); transform:translateY(-2px); }

        @media (max-width:640px) {
          .hero-btns { flex-direction:column !important; align-items:stretch !important; }
          .hero-btns a { text-align:center; }
          .profiles-grid { grid-template-columns:1fr !important; }
        }
      `}</style>

      {/* ─────────────────────────────────────────────
          MINI NAVBAR
      ───────────────────────────────────────────── */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
        padding: scrollY > 40 ? "10px 32px" : "18px 32px",
        background: scrollY > 40 ? "rgba(13,11,30,.95)" : "transparent",
        backdropFilter: scrollY > 40 ? "blur(16px)" : "none",
        boxShadow: scrollY > 40 ? "0 1px 0 rgba(255,255,255,.06)" : "none",
        transition: "all .4s cubic-bezier(.16,1,.3,1)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src={AkasbLogo} alt="AKASB" style={{ height: 30 }} />
          <span style={{ fontFamily: "'Anton', sans-serif", fontSize: 18, color: "#fff", letterSpacing: 3 }}>AKASB</span>
        </Link>
        <Link to="/" style={{
          display: "flex", alignItems: "center", gap: 8,
          color: "rgba(255,255,255,.6)", fontSize: 12, fontWeight: 600,
          letterSpacing: 1, textTransform: "uppercase", transition: "color .2s",
        }}
        onMouseEnter={e => e.currentTarget.style.color = "#E4B428"}
        onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,.6)"}>
          <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: 10 }} /> Voltar ao Site
        </Link>
      </nav>

      {/* ─────────────────────────────────────────────
          HERO — dark geometric, sem imagem de fundo
      ───────────────────────────────────────────── */}
      <section style={{
        position: "relative", minHeight: "100svh",
        background: "#0d0b1e",
        display: "flex", alignItems: "center",
        overflow: "hidden",
      }}>
        {/* ── Animated grid background ── */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          backgroundImage: "repeating-linear-gradient(rgba(255,255,255,.028) 0px, rgba(255,255,255,.028) 1px, transparent 1px, transparent 72px), repeating-linear-gradient(90deg, rgba(255,255,255,.028) 0px, rgba(255,255,255,.028) 1px, transparent 1px, transparent 72px)",
        }} />

        {/* ── Gold horizontal accent line ── */}
        <div style={{ position: "absolute", top: "42%", left: 0, right: 0, height: 1, background: "linear-gradient(to right, transparent, rgba(228,180,40,.12) 20%, rgba(228,180,40,.12) 80%, transparent)", pointerEvents: "none" }} />

        {/* ── Glows ── */}
        <div className="anim-glow" style={{ position: "absolute", top: "-20%", right: "-5%", width: "55vw", height: "55vw", maxWidth: 700, borderRadius: "50%", background: "radial-gradient(circle, rgba(228,180,40,.09) 0%, transparent 60%)", pointerEvents: "none" }} />
        <div className="anim-glow" style={{ position: "absolute", bottom: "-15%", left: "-5%", width: "45vw", height: "45vw", maxWidth: 560, borderRadius: "50%", background: "radial-gradient(circle, rgba(79,142,247,.07) 0%, transparent 60%)", pointerEvents: "none", animationDelay: "2s" }} />

        {/* ── Rotating outer ring — desktop ── */}
        <div className="anim-ring" style={{
          position: "absolute", top: "8%", right: "5%",
          width: "clamp(220px, 32vw, 480px)", height: "clamp(220px, 32vw, 480px)",
          border: "1px solid rgba(228,180,40,.1)", borderRadius: "50%", pointerEvents: "none",
        }}>
          <div style={{ position: "absolute", top: -5, left: "50%", width: 10, height: 10, borderRadius: "50%", background: "#E4B428", transform: "translateX(-50%)", boxShadow: "0 0 16px #E4B428" }} />
          <div style={{ position: "absolute", bottom: -5, left: "50%", width: 6, height: 6, borderRadius: "50%", background: "rgba(228,180,40,.4)", transform: "translateX(-50%)" }} />
        </div>
        {/* Inner ring */}
        <div style={{
          position: "absolute", top: "calc(8% + 32px)", right: "calc(5% + 32px)",
          width: "calc(clamp(220px, 32vw, 480px) - 64px)", height: "calc(clamp(220px, 32vw, 480px) - 64px)",
          border: "1px solid rgba(228,180,40,.05)", borderRadius: "50%", pointerEvents: "none",
          animation: "rotateSlow 18s linear infinite reverse",
        }}>
          <div style={{ position: "absolute", top: "20%", right: -4, width: 8, height: 8, borderRadius: "50%", background: "rgba(79,142,247,.6)" }} />
        </div>

        {/* ── Gold vertical accent ── */}
        <div style={{ position: "absolute", top: 0, left: "7vw", width: 1, height: "100%", background: "linear-gradient(to bottom, transparent, rgba(228,180,40,.2) 25%, rgba(228,180,40,.2) 75%, transparent)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: "25%", left: "7vw", width: 8, height: 8, borderRadius: "50%", background: "#E4B428", transform: "translateX(-3.5px)", boxShadow: "0 0 12px #E4B428" }} />
        <div style={{ position: "absolute", top: "75%", left: "7vw", width: 5, height: 5, borderRadius: "50%", background: "rgba(228,180,40,.4)", transform: "translateX(-2px)" }} />

        {/* ── Content ── */}
        <div style={{ position: "relative", zIndex: 2, width: "100%", padding: "clamp(100px, 14vh, 130px) 7vw clamp(80px, 12vh, 110px)" }}>
          {/* Badge */}
          <div className="anim-badge" style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#E4B428", boxShadow: "0 0 12px #E4B428" }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: "#E4B428", letterSpacing: 3, textTransform: "uppercase" }}>Plataforma Digital · AKASB</span>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#E4B428", boxShadow: "0 0 12px #E4B428" }} />
          </div>

          {/* Title */}
          <div className="anim-title">
            <div style={{ display: "flex", alignItems: "center", gap: "clamp(12px, 3vw, 24px)", marginBottom: 8, flexWrap: "wrap" }}>
              <img src={AkasbLogo} alt="AKASB" style={{ height: "clamp(44px, 8vw, 72px)", filter: "drop-shadow(0 4px 20px rgba(228,180,40,.5))" }} />
              <h1 style={{ fontFamily: "'Anton', sans-serif", fontSize: "clamp(3rem, 13vw, 8.5rem)", color: "#fff", lineHeight: .88, letterSpacing: "clamp(2px, 1vw, 8px)", textShadow: "0 4px 40px rgba(0,0,0,.3)" }}>
                CONNECT
              </h1>
            </div>
            <div style={{ height: 3, width: "clamp(80px, 15vw, 180px)", background: "linear-gradient(to right, #E4B428, transparent)", borderRadius: 2, marginBottom: 24 }} />
          </div>

          {/* Description */}
          <p className="anim-sub" style={{ fontSize: "clamp(14px, 2.2vw, 18px)", color: "rgba(255,255,255,.55)", lineHeight: 1.85, marginBottom: "clamp(40px, 7vh, 60px)", maxWidth: 520, fontWeight: 300 }}>
            A plataforma digital da AKASB — para encarregados de educação e direção do clube acompanharem e gerirem tudo, em qualquer lugar.
          </p>

          {/* ── BIG CTA CARDS — hero ── */}
          <div className="anim-btns" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))", gap: 14, maxWidth: 560 }}>
            {/* Entrar */}
            <Link to="/connect/login" style={{ display: "block" }}>
              <div style={{
                background: "#E4B428", borderRadius: 12, padding: "22px 24px",
                position: "relative", overflow: "hidden",
                transition: "all .3s cubic-bezier(.16,1,.3,1)",
                boxShadow: "0 10px 40px rgba(228,180,40,.35)",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 20px 56px rgba(228,180,40,.5)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 10px 40px rgba(228,180,40,.35)"; }}>
                <div style={{ position: "absolute", bottom: -16, right: -8, fontFamily: "'Anton', sans-serif", fontSize: 80, color: "rgba(0,0,0,.07)", lineHeight: 1, userSelect: "none" }}>01</div>
                <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <div>
                    <div style={{ fontFamily: "'Anton', sans-serif", fontSize: "clamp(1.2rem, 3vw, 1.6rem)", color: "#0d0b1e", textTransform: "uppercase", letterSpacing: 2, marginBottom: 4 }}>Entrar</div>
                    <div style={{ fontSize: 12, color: "rgba(13,11,30,.6)", fontWeight: 500 }}>Aceder à plataforma</div>
                  </div>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(13,11,30,.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 16, color: "#0d0b1e" }} />
                  </div>
                </div>
              </div>
            </Link>

            {/* Criar Conta */}
            <Link to="/connect/register" style={{ display: "block" }}>
              <div style={{
                background: "rgba(255,255,255,.05)", border: "1.5px solid rgba(255,255,255,.14)",
                borderRadius: 12, padding: "22px 24px",
                position: "relative", overflow: "hidden",
                transition: "all .3s cubic-bezier(.16,1,.3,1)",
                boxShadow: "0 8px 32px rgba(0,0,0,.2)",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.background = "rgba(255,255,255,.09)"; e.currentTarget.style.borderColor = "rgba(228,180,40,.4)"; e.currentTarget.style.boxShadow = "0 16px 48px rgba(0,0,0,.3)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.background = "rgba(255,255,255,.05)"; e.currentTarget.style.borderColor = "rgba(255,255,255,.14)"; e.currentTarget.style.boxShadow = "0 8px 32px rgba(0,0,0,.2)"; }}>
                <div style={{ position: "absolute", bottom: -16, right: -8, fontFamily: "'Anton', sans-serif", fontSize: 80, color: "rgba(255,255,255,.03)", lineHeight: 1, userSelect: "none" }}>02</div>
                <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <div>
                    <div style={{ fontFamily: "'Anton', sans-serif", fontSize: "clamp(1.2rem, 3vw, 1.6rem)", color: "#fff", textTransform: "uppercase", letterSpacing: 2, marginBottom: 4 }}>Criar Conta</div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,.45)", fontWeight: 500 }}>Novo na plataforma</div>
                  </div>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(228,180,40,.1)", border: "1px solid rgba(228,180,40,.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <FontAwesomeIcon icon={faUsers} style={{ fontSize: 15, color: "#E4B428" }} />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="anim-float" style={{ position: "absolute", bottom: 28, right: "7vw", zIndex: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <div style={{ width: 24, height: 40, border: "1.5px solid rgba(255,255,255,.2)", borderRadius: 12, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 5 }}>
            <div style={{ width: 3, height: 7, background: "#E4B428", borderRadius: 2 }} />
          </div>
          <span style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,.25)", letterSpacing: 3, textTransform: "uppercase" }}>Scroll</span>
        </div>
      </section>

      {/* ─────────────────────────────────────────────
          TICKER
      ───────────────────────────────────────────── */}
      <div style={{ background: "#E4B428", padding: "13px 0", overflow: "hidden" }}>
        <div className="ticker-inner">
          {Array(10).fill(["GESTÃO", "PRATICANTES", "AULAS", "PAGAMENTOS", "EVENTOS", "COMUNICAÇÕES", "GRADUAÇÕES", "DASHBOARD"]).flat().map((w, i) => (
            <span key={i} style={{ fontFamily: "'Anton', sans-serif", fontSize: 12, color: "#0d0b1e", letterSpacing: 3, padding: "0 24px", textTransform: "uppercase" }}>
              {w} <span style={{ color: "rgba(13,11,30,.25)", margin: "0 6px" }}>◆</span>
            </span>
          ))}
        </div>
      </div>

      {/* ─────────────────────────────────────────────
          O QUE É O AKASB CONNECT
      ───────────────────────────────────────────── */}
      <section style={{ padding: "clamp(80px, 14vh, 120px) 7vw", background: "#0d0b1e", position: "relative", overflow: "hidden" }}>
        {/* Background grid texture */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none", opacity: .03,
          backgroundImage: "repeating-linear-gradient(rgba(255,255,255,.4) 0px, rgba(255,255,255,.4) 1px, transparent 1px, transparent 60px), repeating-linear-gradient(90deg, rgba(255,255,255,.4) 0px, rgba(255,255,255,.4) 1px, transparent 1px, transparent 60px)",
        }} />

        <div style={{ maxWidth: 1160, margin: "0 auto" }}>
          <Reveal>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", marginBottom: "clamp(56px, 10vh, 80px)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                <div style={{ width: 30, height: 2, background: "#E4B428" }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: "#E4B428", letterSpacing: 3, textTransform: "uppercase" }}>O que é</span>
                <div style={{ width: 30, height: 2, background: "#E4B428" }} />
              </div>
              <h2 style={{ fontFamily: "'Anton', sans-serif", fontSize: "clamp(2.2rem, 6vw, 4.5rem)", color: "#fff", lineHeight: .9, textTransform: "uppercase", marginBottom: 24 }}>
                AKASB Connect
              </h2>
              <p style={{ fontSize: "clamp(14px, 2vw, 17px)", color: "rgba(255,255,255,.5)", maxWidth: 640, lineHeight: 1.9, fontWeight: 300 }}>
                Uma plataforma centralizada que liga encarregados de educação, treinadores e direção do clube — disponível 24h, em qualquer dispositivo.
              </p>
            </div>
          </Reveal>

          {/* Features grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))", gap: 18 }}>
            {FEATURES.map((feat, i) => (
              <Reveal key={feat.title} delay={i * 0.08}>
                <div
                  className="feature-card"
                  onMouseEnter={() => setHoveredCard(i)}
                  onMouseLeave={() => setHoveredCard(null)}
                  style={{
                    background: hoveredCard === i ? "rgba(255,255,255,.07)" : "rgba(255,255,255,.04)",
                    border: `1px solid ${hoveredCard === i ? feat.color + "50" : "rgba(255,255,255,.08)"}`,
                    borderRadius: 16, padding: "28px 24px",
                    boxShadow: hoveredCard === i ? `0 20px 56px rgba(0,0,0,.3), 0 0 0 1px ${feat.color}20` : "0 4px 20px rgba(0,0,0,.2)",
                  }}
                >
                  <div style={{ width: 52, height: 52, borderRadius: 14, background: `${feat.color}18`, border: `1px solid ${feat.color}30`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
                    <FontAwesomeIcon icon={feat.icon} style={{ fontSize: 22, color: feat.color }} />
                  </div>
                  <h3 style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: 16, color: "#fff", marginBottom: 10 }}>{feat.title}</h3>
                  <p style={{ fontSize: 13, color: "rgba(255,255,255,.45)", lineHeight: 1.75 }}>{feat.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────
          PERFIS — Para quem é?
      ───────────────────────────────────────────── */}
      <section style={{ padding: "clamp(80px, 14vh, 120px) 7vw", background: "#080618", overflow: "hidden" }}>
        <div style={{ maxWidth: 1160, margin: "0 auto" }}>
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: "clamp(48px, 8vh, 72px)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginBottom: 20 }}>
                <div style={{ width: 30, height: 2, background: "#E4B428" }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: "#E4B428", letterSpacing: 3, textTransform: "uppercase" }}>Para Quem</span>
                <div style={{ width: 30, height: 2, background: "#E4B428" }} />
              </div>
              <h2 style={{ fontFamily: "'Anton', sans-serif", fontSize: "clamp(2.2rem, 6vw, 4.5rem)", color: "#fff", lineHeight: .9, textTransform: "uppercase" }}>
                Feito Para Si
              </h2>
            </div>
          </Reveal>

          <div className="profiles-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            {PROFILES.map((p, i) => (
              <Reveal key={p.role} delay={i * 0.15} from={i === 0 ? "left" : "right"}>
                <div className="profile-card" style={{
                  background: "linear-gradient(135deg, rgba(255,255,255,.05) 0%, rgba(255,255,255,.02) 100%)",
                  border: "1px solid rgba(255,255,255,.1)",
                  borderRadius: 20, overflow: "hidden",
                  boxShadow: "0 8px 40px rgba(0,0,0,.3)",
                }}>
                  {/* Card header */}
                  <div style={{ padding: "32px 32px 24px", borderBottom: "1px solid rgba(255,255,255,.07)", display: "flex", alignItems: "center", gap: 16 }}>
                    <div style={{ width: 56, height: 56, borderRadius: 14, background: "rgba(228,180,40,.12)", border: "1px solid rgba(228,180,40,.25)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 }}>
                      {p.emoji}
                    </div>
                    <h3 style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: "clamp(15px, 2vw, 18px)", color: "#fff", lineHeight: 1.25 }}>{p.role}</h3>
                  </div>
                  {/* Card body */}
                  <div style={{ padding: "24px 32px 32px", display: "flex", flexDirection: "column", gap: 12 }}>
                    {p.items.map((item) => (
                      <div key={item} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                        <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#E4B428", fontSize: 14, marginTop: 2, flexShrink: 0 }} />
                        <span style={{ fontSize: 13, color: "rgba(255,255,255,.6)", lineHeight: 1.6 }}>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────
          ACESSO RÁPIDO — strip final antes do footer
      ───────────────────────────────────────────── */}
      <section style={{ padding: "clamp(56px, 10vh, 80px) 7vw", background: "#0d0b1e", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 50%, rgba(228,180,40,.05) 0%, transparent 65%)", pointerEvents: "none" }} />
        <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center", position: "relative", zIndex: 1 }}>
          <Reveal>
            <h2 style={{ fontFamily: "'Anton', sans-serif", fontSize: "clamp(1.8rem, 5vw, 3.5rem)", color: "#fff", textTransform: "uppercase", lineHeight: .92, marginBottom: 16 }}>
              Pronto para Começar?
            </h2>
            <p style={{ fontSize: "clamp(13px, 2vw, 16px)", color: "rgba(255,255,255,.4)", marginBottom: 36, lineHeight: 1.8, fontWeight: 300 }}>
              Acede ao teu espaço ou cria conta gratuitamente.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <Link to="/connect/login" className="cta-btn-primary">
                Entrar <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 11 }} />
              </Link>
              <Link to="/connect/register" className="cta-btn-secondary">
                Criar Conta
              </Link>
            </div>
            <div style={{ marginTop: 28 }}>
              <Link to="/" style={{ fontSize: 12, color: "rgba(255,255,255,.25)", fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", transition: "color .2s" }}
              onMouseEnter={e => e.currentTarget.style.color = "rgba(255,255,255,.55)"}
              onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,.25)"}>
                ← Voltar ao site AKASB
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─────────────────────────────────────────────
          MINI FOOTER
      ───────────────────────────────────────────── */}
      <footer style={{ background: "#080618", padding: "32px 7vw", borderTop: "1px solid rgba(255,255,255,.05)" }}>
        <div style={{ maxWidth: 1160, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src={AkasbLogo} alt="AKASB" style={{ height: 28 }} />
            <span style={{ fontFamily: "'Anton', sans-serif", fontSize: 16, color: "rgba(255,255,255,.4)", letterSpacing: 2 }}>AKASB</span>
          </Link>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,.2)" }}>
            Copyright © AKASB {new Date().getFullYear()} · Todos os direitos reservados
          </p>
        </div>
      </footer>
    </>
  );
}
